/**
 * Constants and mappings for formatting audit log data.
 */

// Constants for path patterns used in campaign data flattening
export const CAMPAIGN_PATH_PATTERNS = ['campaign', '.campaign'];
export const FSM_UNDER_CAMPAIGN_PATTERNS = ['campaign.fsm', '.campaign.fsm'];

// Words that should always be displayed in all caps
export const ALL_CAPS_WORDS = [
  '2FA',
  'AI',
  'AMP',
  'API',
  'BCC',
  'CDP',
  'CNAME',
  'CS',
  'CSM',
  'CSS',
  'DKIM',
  'DMARC',
  'DNS',
  'HIPAA',
  'HTML',
  'HSTS',
  'HTTP',
  'HTTPS',
  'ID',
  'IDs',
  'IP',
  'JSON',
  'OTP',
  'SMS',
  'SMTP',
  'SDK',
  'SPF',
  'SSO',
  'STO',
  'TLS',
  'TS',
  'UI',
  'URI',
  'URL',
  'UUID',
  'WYSIWYG',
];

// Raw attribute name mappings (for snake_case/camelCase before formatting)
// Note: "Environment" is the legacy backend term for "Workspace" in the UI
export const RAW_ATTRIBUTE_MAPPINGS: Record<string, string> = {
  env: 'Workspace',
  env_id: 'Workspace ID',
  environment: 'Workspace',
  environment_id: 'Workspace ID',
  last_environment_id: 'Last Workspace',
  lastenvironmentid: 'Last Workspace',
  hipaasms: 'HIPAA SMS',
  cid: 'CID',
  ip_addr: 'IP Address',
  ua: 'User Agent',
  internal1: 'Profile 1',
  internal2: 'Profile 2',
};

// Abbreviation expansions (applied after initial formatting)
// Maps abbreviated words to their full forms
export const ABBREVIATION_EXPANSIONS: Record<string, string> = {
  Attr: 'Attribute',
  Attrs: 'Attributes',
  Liquidjs: 'LiquidJS',
  Msg: 'Message',
  Rd: 'Read',
  Seg: 'Segment',
  TS: 'Technical Support',
  Tz: 'Timezone',
};

// Campaign-specific attribute name mappings
// These transform formatted attribute names to more user-friendly versions
// Terminology follows the ~/code/ui codebase (action-info.ts, VWB components)
export const CAMPAIGN_ATTRIBUTE_MAPPINGS: Record<string, string> = {
  // Trigger-related
  'Etype Trigger Name': 'Event Name',
  'Etype Trigger Type': 'Trigger Type',

  // Workspace (legacy backend uses "Environment", UI uses "Workspace")
  Env: 'Workspace',
  'Env ID': 'Workspace ID',
  'Env Id': 'Workspace ID',
  Environment: 'Workspace',
  'Environment ID': 'Workspace ID',
  'Environment Id': 'Workspace ID',

  // Frequency settings (UI uses "Frequency" section, restart_mode options)
  'Restart Mode': 'Frequency',
  'Restart Min Interval': 'Frequency Interval (seconds)',

  // Exit conditions (UI uses "Exit" section)
  'Exit On Trigger Or Filter Not Matched': 'Exit When Trigger Unmatched',
  'Exit On Conversion Matched': 'Exit When Conversion Criteria Met',
  'Exit Only After Message': 'Exit Only After Message Sent',
  'Global Exit Conditions': 'Exit Conditions',

  // Subscription settings
  'Send To Unsubscribed': 'Send to Unsubscribed',
  'Send To Unsubs': 'Send to Unsubscribed',

  // Message limits
  'Use Message Limits': 'Count Toward Message Limits',
  'Use Workspace Message Limits Retry': 'Use Campaign Retry Settings',
  'Message Limits Retry Window Secs': 'Retry Window (seconds)',
  'Override Global Message Limits Retry': 'Override Campaign Retry Settings',

  // Date triggered
  'Date Triggered Frequency': 'Trigger Frequency',
  'Date Triggered Lead Days': 'Days Before/After Date',
  'Date Triggered Attribute': 'Date Attribute',
  'Date Triggered Start Time': 'Send Time',
  'Date Triggered Zone': 'Timezone',
  'Date Triggered Fallback Zone': 'Fallback Timezone',

  // Conversion/Goal (UI uses "Goal" section with "conversion criteria" text)
  'Conversion Type': 'Conversion Criteria Type',
  'Conversion Action': 'Conversion Criteria Action',
  'Conversion Segment ID': 'Conversion Criteria Segment',
  'Conversion Window': 'Conversion Window (seconds)',
  'Conversion Event Name': 'Conversion Criteria Event',
  'Conversion Event Filters': 'Conversion Criteria Event Filters',
  'Has Conversion': 'Has Conversion Criteria',

  // Object/Relationship campaigns
  'Object Campaign': 'Object Trigger',
  'Relationship Campaign': 'Relationship Trigger',
  'Object Attribute Triggers': 'Object Trigger Conditions',
  'Relationship Attribute Triggers': 'Relationship Trigger Conditions',
  'Object Filters': 'Object Filters',
  'Relationship Filters': 'Relationship Filters',
  'Trigger Person Filters': 'Person Filters',
  'Trigger Relationship Filters': 'Relationship Filters',

  // Audience (UI uses "Audience" section)
  Audience: 'Ad Audience',
  'Person Filters': 'Person Filters',

  // Campaign Action attributes
  'Sending State': 'Status',
  'Action State': 'Action Status',
  'Exit Type': 'Exit Behavior',
  'Delay Event Attr': 'Wait Until Attribute',
  'Delay Event Attribute': 'Wait Until Attribute',
  'Delay Event Attr Offset': 'Wait Until Offset',
  'Delay Event Attribute Offset': 'Wait Until Offset',
  'Multi Conditional Filter': 'Multi-Split Branch Conditions',
  'Conditional Filter': 'True/False Branch Conditions',
  'Multi Conditions': 'Multi-Split Branch Conditions',
  Preconditions: 'Entry Conditions',

  // Action types display (UI action-info.ts terminology)
  'Sub Type': 'Action Subtype',

  // Collection query
  'Query Collection': 'Query Collection',
  'Order By': 'Sort By',
  'Single Result': 'Return Single Result',

  // Webhook action
  'Request Method': 'HTTP Method',
  'Await Completion': 'Wait for Response',
  'Can Convert': 'Can Trigger Conversion',
  'Attributes Names': 'Attribute Names',
  'Attributes Values': 'Attribute Values',
  'Attribute Destinations': 'Save To',

  // A/B Test (UI uses "A/B Test" and "Experiment")
  'Split State': 'Experiment Status',
  'Split Started': 'Experiment Started',
  'Split Fsm': 'Experiment State',
  Allocations: 'Traffic Allocation',
  Holdout: 'Holdout Test',

  // Random Cohort Branch (UI terminology)
  Cohorts: 'Path Percentages',
  'Cohort Names': 'Path Names',

  // Time Delay (UI uses "Time Delay" for delay_seconds_action)
  // Values are now formatted as human-readable durations (e.g., "8 days")
  Delay: 'Time Delay',
  'Minimum Delay': 'Minimum Delay',
  'Maximum Delay': 'Maximum Delay',

  // Time Window (UI uses "Time Window" for delay_time_window_action)
  'Start Hour': 'Window Start Hour',
  'End Hour': 'Window End Hour',
  'Start Minutes': 'Window Start Minutes',
  'End Minutes': 'Window End Minutes',
  'Start Time': 'Window Start Time',
  'End Time': 'Window End Time',
  'Days Of Week': 'Window Days',
  Zone: 'Timezone',
  'Fallback Zone': 'Fallback Timezone',
  'Use Customer Zone': 'Use Customer Timezone',

  // Optimal Time
  'Optimal Time Days': 'Optimal Send Time Days',
  'Optimal Time Start': 'Optimal Send Time Start',
  'Optimal Time End': 'Optimal Send Time End',
  'Optimal Time Description': 'Optimal Send Time Description',

  // LLM action (UI uses "Run LLM")
  'Llm Calls': 'AI Model Calls',
  'Llm Result Action': 'AI Result Action',

  // Template references
  'Template ID': 'Template',
  'Test Template IDs': 'A/B Test Templates',

  // FSM/Workflow
  Fsm: 'Workflow State',
  Edges: 'Workflow Connections',
  'Workflow Action IDs': 'Actions',
  'Next Action IDs': 'Next Actions',
  'Sub Action IDs': 'Child Actions',
  'Parent ID': 'Parent Action',
  'Actions In Branches': 'Branch Actions',

  // Create or Update Person (UI terminology)
  'Attribute Name': 'Person Attribute Name',
  'Attribute Value': 'Person Attribute Value',

  // Manual Segment Update (UI terminology)
  'Add To Seg': 'Add to Manual Segment',
  'Segment ID': 'Segment',

  // Send Event action (UI uses "Send Event")
  'Event Name': 'Event Name',

  // Batch Update
  'Batch Update': 'Batch Update',

  // Grace Period
  'Debounce Secs': 'Grace Period (seconds)',

  // =============================================================================
  // Newsletter-specific attribute mappings
  // Terminology follows ~/code/ui/app/models/newsletter.ts
  // =============================================================================

  // Recipients/Audience
  'Segment Groups': 'Recipients',
  'Send To Everyone': 'Send to everyone',

  // A/B Testing
  'Send Percentage': 'Test sample percentage',
  'Test Metric': 'Winning metric',
  'Test Duration': 'Test duration (seconds)',
  'Test Ended At': 'Test ended',
  'Winner Template ID': 'Winning template',
  'Automatic Winner': 'Automatic winner selection',

  // Send Time Optimization
  'STO Enabled': 'Send time optimization',
  'STO Recipient List Set': 'STO recipients prepared',
  'Optimal Date Time': 'Optimal send time',
  'Optimal Date Time Selected': 'Use optimal send time',
  'Optimal Date Time Explanation': 'Optimal time explanation',

  // Timezone matching
  'Tz Match Enabled': 'Local timezone delivery',
  'Tz Match Anchor Time': 'Timezone anchor time',

  // Rate limiting
  'Rate Limit Email Rate': 'Send rate (per period)',
  'Rate Limit Time Period': 'Send rate period (seconds)',

  // Tracking & limits
  Tracked: 'Link tracking',
  Deduped: 'Deduplicate recipients',

  // Subscription settings
  'Subscription Topic IDs': 'Subscription topics',
  'Subscription Topic ID': 'Subscription topic',

  // Template references
  'Template IDs': 'Templates',

  // Send status
  'Recipient List Set': 'Recipient list prepared',
  'Original Recipient Count': 'Original recipient count',
  'Scheduled Send To Remaining': 'Schedule remaining recipients',
  'Sent To Remaining At': 'Sent to remaining recipients',
  'Sends Completed': 'Sends completed',
  'Attempted Count': 'Attempted sends',

  // State
  'Is Paused': 'Paused',
  'Cancelled At': 'Cancelled',
  'Sent At': 'Sent',
  'Scheduled At': 'Scheduled for',
  'Scheduled Timezone': 'Scheduled timezone',
  'Original Scheduled At': 'Originally scheduled for',
};

// UUID regex pattern - compiled once at module level
export const UUID_PATTERN = /^[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12}$/i;

// IP address field names (normalized - lowercase, no underscores/hyphens)
export const IP_FIELD_NAMES = new Set(['ipaddr', 'ipaddress', 'ip']);

// User agent field names (normalized - lowercase, no underscores/hyphens)
export const USER_AGENT_FIELD_NAMES = new Set(['useragent', 'ua']);
